import {CSVTableToolbar, Icon, MultilineEditor} from '@dv5/components'
import {DocumentPart, PartType} from '@dv5/domain'
import React, {FC, useState} from "react"
import {getPreviewPart} from "./documentPartPreview"
import {DocumentPartViewer} from "./DocumentPartViewer"
import {PartSaveStatus} from "./PartSaveStatus"
import {DiaryEditor} from "./diary/DiaryEditor"
import {getFocusButtonTitle, getFocusIcon, useFullscreenEditor} from "./useFullscreenEditor"
import './DocumentPartEditor.less'

interface Props {
    part: DocumentPart
    partType: PartType
    content: string
    setContent: (content: string) => void
    saveStatus: 'not_changed' | 'changed' | 'saved'
    lastSavedTime: Date | null
    handleManualSave: () => void
    cancelEdit: () => void
}

export const DocumentPartEditor: FC<Props> = ({
                                                  part,
                                                  partType,
                                                  content,
                                                  setContent,
                                                  handleManualSave,
                                                  cancelEdit,
                                                  saveStatus,
                                                  lastSavedTime,
                                              }) => {

    const [cursorPosition, setCursorPosition] = useState<{ row: number; col: number }>({ row: 0, col: 0 })
    const {isFullscreen, activeTab, setActiveTab, toggleFullscreen, exitFullscreen} = useFullscreenEditor()

    const renderEditor = () => {
        if (partType === PartType.DIARY) {
            return (
                <DiaryEditor partId={part.id} content={content} setContent={setContent}/>
            )
        }

        return (
            <>
                {partType === PartType.CSV_TABLE &&
                    <CSVTableToolbar content={content} setContent={setContent} cursorPosition={cursorPosition}/>}
                <MultilineEditor value={content}
                                 setValue={setContent}
                                 onConfirm={handleManualSave}
                                 onCancel={isFullscreen ? exitFullscreen : cancelEdit}
                                 onCursorChange={setCursorPosition}
                                 settings={{
                                     autoResize: true,
                                     lineNumbers: true,
                                     autoFocus: true,
                                     allowFullScreen: !isFullscreen,
                                 }}
                                 disabled={false}
                />
                <PartSaveStatus saveStatus={saveStatus} lastSavedTime={lastSavedTime}/>
            </>
        )
    }

    const changeFromPreview = (partId: string, newContent: string) => {
        setContent(newContent)
    }

    return (
        <div className="document-part-editor-wrapper">
            {!isFullscreen && (
                <button
                    type="button"
                    className="document-part-editor__focus-button"
                    onClick={toggleFullscreen}
                    title={getFocusButtonTitle(false)}
                >
                    <Icon name={getFocusIcon(false)} width="16" height="16"/>
                </button>
            )}
            {!isFullscreen && renderEditor()}
            {isFullscreen && (
                <div className="document-part-editor--fullscreen">
                    <div className="document-part-editor__fullscreen-header">
                        <div className="document-part-editor__tabs">
                            <button
                                type="button"
                                className={`document-part-editor__tab${activeTab === 'edit' ? ' document-part-editor__tab--active' : ''}`}
                                onClick={() => setActiveTab('edit')}
                            >
                                Edit
                            </button>
                            <button
                                type="button"
                                className={`document-part-editor__tab${activeTab === 'preview' ? ' document-part-editor__tab--active' : ''}`}
                                onClick={() => setActiveTab('preview')}
                            >
                                Preview
                            </button>
                        </div>
                        <button
                            type="button"
                            className="document-part-editor__focus-button"
                            onClick={exitFullscreen}
                            title={getFocusButtonTitle(true)}
                        >
                            <Icon name={getFocusIcon(true)} width="16" height="16"/>
                        </button>
                    </div>
                    <div className="document-part-editor__fullscreen-body">
                        {activeTab === 'edit' ? (
                            renderEditor()
                        ) : (
                            <DocumentPartViewer part={getPreviewPart(part, content)} onChange={changeFromPreview}/>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}